import { LabelNode, RuleContext, TemplateLabel } from '@navstation-portal/reporting/data-model/ui';
import { evaluate } from './evaluate';
import { RuleExplanationLine, explainNode } from './explain-rule';

/**
 * DEV-ONLY. Explains how a label tree chose its text. It is the label-tree twin
 * of `explainRule`: it walks the branch that was taken, and it asks the rule
 * tree the same questions the resolver asked.
 *
 * It does not change how a label resolves. `resolveLabel` stays the one answer,
 * and this describes it. Delete this with the rest of the form-rule debug UI.
 *
 * A plain string label gives no lines. There is nothing to explain: the text is
 * the label.
 */
export function explainLabel(label: TemplateLabel | undefined, context: RuleContext): RuleExplanationLine[] {
  if (label === undefined || typeof label === 'string') {
    return [];
  }
  return explainLabelNode(label, context, 0);
}

function explainLabelNode(node: LabelNode, context: RuleContext, depth: number): RuleExplanationLine[] {
  switch (node.type) {
    case 'text':
      return [{ type: node.type, detail: `"${node.value}"`, result: true, depth }];
    case 'when': {
      const taken = evaluate(node.when, context);
      const parent: RuleExplanationLine = {
        type: node.type,
        detail: taken ? 'then' : 'else',
        result: taken,
        depth,
      };
      const rule = explainNode(node.when, context, depth + 1);
      const branch = taken ? node.then : node.else;
      // A false rule with no else branch gives no text, so the form falls back
      // to the markup. The line says so, or the tooltip stops with no reason.
      const chosen: RuleExplanationLine[] =
        branch === undefined
          ? [{ type: 'none', detail: 'no else branch, so the markup label shows', result: false, depth: depth + 1 }]
          : explainLabelNode(branch, context, depth + 1);
      return [parent, ...rule, ...chosen];
    }
    default:
      return assertNever(node);
  }
}

function assertNever(node: never): never {
  throw new Error(`Unknown label node type: ${String((node as { type: string }).type)}`);
}
